import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';

import { ReadFile, withKey } from '@paragon/client-tools';
import { Seal, UnsavedSealWithoutRegion } from '@paragon/contract-library';
import { enqueueError } from '@paragon/notification-tools';

import { removeSeal } from '../../actions/sealsAndSignaturesActions';
import { sealForSelectedRegionForLoggedInUser } from '../../selectors/sealRegions';
import { UserFromSelector } from '../../selectors/users';
import { RootState } from '../../store';
import DateTextbox from '../tools/DateTextbox';
import Upload from '../tools/Upload';

interface SealOverride {
  readonly renewalDate?: string;
  readonly image?: string;
  readonly userEmail?: string;
  readonly requiredHeightInInches?: number | string;
  readonly requiredWidthInInches?: number | string;
  readonly originalHeightInPixels?: number;
  readonly originalWidthInPixels?: number;
}

interface OwnProps {
  readonly onSave: (seal: Seal | UnsavedSealWithoutRegion) => void;
  readonly user: UserFromSelector;
}
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = OwnProps & StateProps & DispatchProps;

interface State {
  readonly isOverlayShown: boolean;
  readonly isSealImageLoaded: boolean;
  readonly sealOverride: SealOverride | null;
}

const defaultState: State = {
  isOverlayShown: false,
  isSealImageLoaded: false,

  sealOverride: null,
};

class SealInfo extends React.Component<Props, State> {
  state: State = {
    ...defaultState,
    isSealImageLoaded: !!this.props.seal,
  };

  onDateChanged = (date: Date) => {
    this.setSealProperty('renewalDate', date.toISOString());
  };

  get seal() {
    return this.state.sealOverride ? this.state.sealOverride : this.props.seal;
  }

  setSealProperty<K extends keyof SealOverride>(key: K, value: SealOverride[K], callback?: () => void) {
    this.setState(
      (state) => ({
        sealOverride: {
          ...this.props.seal,
          ...state.sealOverride,
          [key]: value,
        },
      }),
      callback,
    );
  }

  onWidthChanged: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    if (event.target.value.length === 0 || !Number.isNaN(parseFloat(event.target.value))) {
      this.setSealProperty('requiredWidthInInches', event.target.value);
    }
  };

  onHeightChanged: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (event) => {
    if (event.target.value.length === 0 || !Number.isNaN(parseFloat(event.target.value))) {
      this.setSealProperty('requiredHeightInInches', event.target.value);
    }
  };

  onSaveClick = () => {
    const { sealOverride } = this.state;
    if (!sealOverride) {
      return;
    }

    if (sealOverride.renewalDate == null || Number.isNaN(Date.parse(sealOverride.renewalDate))) {
      this.props.enqueueError('Invalid date.');
    } else if (sealOverride.image!.length <= 1) {
      this.props.enqueueError('Invalid image.');
    } else if (Number.isNaN(parseFloat(sealOverride.requiredWidthInInches as string))) {
      this.props.enqueueError('Invalid width');
    } else if (Number.isNaN(parseFloat(sealOverride.requiredHeightInInches as string))) {
      this.props.enqueueError('Invalid height');
    } else {
      let seal: Seal | UnsavedSealWithoutRegion = {
        renewalDate: sealOverride.renewalDate!,
        image: sealOverride.image!,
        userEmail: this.props.user.email,
        requiredHeightInInches: sealOverride.requiredHeightInInches!,
        requiredWidthInInches: sealOverride.requiredWidthInInches!,
        originalHeightInPixels: this.props.seal?.originalHeightInPixels,
        originalWidthInPixels: this.props.seal?.originalWidthInPixels,
      };
      if (this.props.seal?.id) seal = { ...seal, id: this.props.seal.id };
      this.props.onSave(seal);
    }
  };

  removeSeal = () => {
    this.props.removeSeal(this.props.seal!.id);
    this.setState(defaultState);
  };

  loadSealImage = async (files: readonly Promise<ReadFile>[]) => {
    try {
      const loadedFile = await files[0];
      this.setSealProperty('image', loadedFile.contents, () => {
        this.setState({
          isSealImageLoaded: true,
        });
      });
    } catch (err) {
      console.error(err);
    }
  };

  renderSealComponent() {
    return this.state.isSealImageLoaded ? (
      <Box component="img" sx={{ maxWidth: 200 }} src={this.seal!.image} alt="Seal" />
    ) : (
      <Typography>No Seal Uploaded</Typography>
    );
  }

  renderOverlayItems() {
    return this.state.isSealImageLoaded ? (
      <Box sx={{ display: 'flex' }}>
        <Upload
          onUpload={this.loadSealImage}
          buttonSx={{ m: 1 }}
          id="replace"
          buttonText="Replace"
          accept="image/*"
          multiple={false}
        />
        <Button sx={{ m: 1 }} variant="outlined" onClick={this.removeSeal}>
          Remove
        </Button>
      </Box>
    ) : (
      <Upload
        onUpload={this.loadSealImage}
        buttonSx={{ m: 1 }}
        id="upload-seal"
        buttonText="Upload"
        accept="image/*"
        multiple={false}
      />
    );
  }

  renderSealImage() {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-evenly' }}>
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {this.renderSealComponent()}
          {this.renderOverlayItems()}
        </Box>
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', p: 1 }}>
          <TextField
            variant="standard"
            id="width"
            label="Width in Inches"
            sx={{ mx: 1, maxWidth: 200 }}
            value={this.seal?.requiredWidthInInches}
            onChange={this.onWidthChanged}
            margin="normal"
          />
          <TextField
            variant="standard"
            id="height"
            label="Height in Inches"
            sx={{ mx: 1, maxWidth: 200 }}
            value={this.seal?.requiredHeightInInches}
            onChange={this.onHeightChanged}
            margin="normal"
          />
          <Button variant="outlined" onClick={this.onSaveClick}>
            Save
          </Button>
        </Box>
      </Box>
    );
  }

  render() {
    return (
      <div>
        <Typography sx={{ mb: 1 }}>{`Selected Region: ${this.props.selectedRegion.name}`}</Typography>
        <Paper sx={{ p: 2, bgcolor: 'backContrast.light', display: 'flex', flexDirection: 'column' }} elevation={0}>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-around', p: 1 }}>
            <DateTextbox sx={{ mx: 1, maxWidth: 200 }} value={this.seal?.renewalDate} onChange={this.onDateChanged} />
          </Box>
          {this.renderSealImage()}
        </Paper>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  selectedRegion: state.sealsAndSignatures.selectedRegion!,
  seal: sealForSelectedRegionForLoggedInUser(state),
});

const actionCreators = {
  removeSeal,
  enqueueError,
};
// When the seal region changes, we want to reset the seal input.
const keySelector = (props: Props) => props.seal?.region ?? '';

export default connect(mapStateToProps, actionCreators)(withKey(keySelector, SealInfo));
